/**
 * Welcome Page Component
 * Displays the main information of the website, including mission, vision, and values.
 * The page is structured in sections: hero, mission, vision, values, and impact.
 *
 * Author: Meng Li, Jennifer Tran
 * Created on: 2024-10-16
 * Last updated on: 2025-01-24
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React from 'react';
import { Link } from "react-router-dom";
import { MissionVisionSection, ValuesSection } from '../components/MissionVisionValues';
import '../../assets/css/Welcome.css';
import '../styles/Global.css';
import '../styles/Reusable.css';

// Image imports
import ManPushingCart from '../images/Welcome/real_join.jpg';
import ThreePeople from '../images/Welcome/clipart_social_justice.png';
import ManSleeping from '../images/Welcome/real_four_s.jpg';

// Component sections
const HeroSection = () => (
    <div className="hero-section">
        <div className="text-section">
            <h1 className="h1 title-text">SimpliCITY</h1>
            <p className="p card-text">
                We aim to improve resource and service accessibility for our unhoused neighbours.
                We hope to unify the city and its individuals through sustainable innovation and social impact.
            </p>
        </div>
        <div className="image-section">
            <img
                src={ManPushingCart}
                alt="Homeless individual pushing cart"
                className="image"
            />
        </div>
    </div>
);

const JoinSection = () => (
    <div className="join-section">
        <p className="p card-text">
            We're not just pointing the way;
        </p>
        <p className="new-line"></p>
        <p className="p card-text">
            we're walking the path together.
        </p>
        {/* Link to navigate to the Contact page */}
        <Link to="/contact">
            <button className="btn btn-primary join-button">Join Us</button>
        </Link>
    </div>
);

const InspirationQuote = () => (
    <div className="quote-section">
        <h2 className="h2 title-text">
            "A measure of a good society is the way
        </h2>
        <p className="new-line"></p>
        <h2 className="h2 title-text">
            it treats its most vulnerable population."
        </h2>
        <h3 className="h3 author-text">Mahatma Gandhi</h3>
    </div>
);

const SocialJusticeBanner = () => (
    <div className="justice-banner">
        <h2 className="h2 banner-text">Homelessness is a social justice problem.</h2>
    </div>
);

const ResponsibilitySection = () => (
    <div className="responsibility-section">
        <div className="row">
            <div className="column illustration">
                <img src={ThreePeople} alt="Illustration of 3 people"/>
            </div>
            <div className="column text">
                <h3 className="h3 title-text">That means we all have a responsibility</h3>
                <div className="p card-text">
                    <p>
                        In Canada, homelessness has become a public health
                        crisis, with approximately 35,000 Canadians facing
                        homelessness nightly, and at least 235,000 annually.
                    </p>
                    <p className="new-line"></p>
                    <p>
                        There are various factors that make some of us thrive
                        in society while pushing others to the margins. Some
                        of these are individual, but most are social, economic,
                        political and historical.
                    </p>
                    <p className="new-line"></p>
                    <p>
                        All of this to say, it's not a me or you problem–it's an
                        US problem and requires an US solution.
                    </p>
                </div>
            </div>
        </div>
    </div>
);

const ImpactSection = () => {
    const IMPACT_STATEMENTS = [
        "Sparking solidarity",
        "Supporting our society",
        "Strengthening our cities",
        "Seeking sustainable change"
    ];

    return (
        <div className="impact-section">
            <div className="image-section">
                <img src={ManSleeping} alt="Person sleeping outside"/>
            </div>
            <div className="statements-section">
                {IMPACT_STATEMENTS.map((statement, index) => (
                    <div key={index} className="impact-statement">
                        {statement}
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Main Welcome component that combines all sections
 * Sections are arranged in a logical flow to tell the story
 * of SimpliCITY's mission and impact
 */
const Welcome = () => (
    <div className="welcome-page">
        <HeroSection />
        <JoinSection />
        <InspirationQuote />
        <SocialJusticeBanner />
        <ResponsibilitySection />
        <MissionVisionSection />
        <ValuesSection />
        <ImpactSection />
    </div>
);

export default Welcome;
